<template>
  <!------------------------------------------
    Grantee list for the print version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-state-print-grantees">
    <div>
      <div class="grantee-list-header">LSC Grantees</div>
      <div class="grantee-list">
        <div class="grantee-list-item" v-for="(g, index) in granteesByType.bfGeneral.filter((v, i, a) => a.findIndex(t => t.org_name === v.org_name) === i)" :key="index">
          <div class="grantee-list-item-header">
            <v-icon size="12" left :color="colorPerServArea.filter(x => x.area === g.serv_area)[0].color">
              {{ circleIcon }}
            </v-icon>
            <div>
              {{ g.org_name }}
              <span class="a-n-symbol" v-if="granteesByType.agWorker.map(x => x.org_name).indexOf(g.org_name) > -1">(A)</span>
              <span class="a-n-symbol" v-if="granteesByType.nativeAm.map(x => x.org_name).indexOf(g.org_name) > -1">(N)</span>
            </div>
          </div>
          <div class="main-office" v-if="granteesByType.bfGeneral.length < 4">
            Main office: 
            <span class="main-office-name">{{ mainOfficePerGrantee[g.recipient_id] }}</span>
            <v-icon color="#b31308" size="18">
              {{ mainOfficeIcon }}
            </v-icon>
          </div>
          <div class="branch-offices" v-if="granteesByType.bfGeneral.length === 1 && g.recipient_id in sortedBranchesPerGrantee && sortedBranchesPerGrantee[g.recipient_id].length > 0">
            Branch offices: <span class="main-office-name">{{ sortedBranchesPerGrantee[g.recipient_id].join(', ') }}</span>
          </div>
        </div>
        <div class="other-a-n-grantee" v-if="outOfStateBothAgAndNativeAm.length > 0">
          <div>Also serving Native Americans and agricultural workers:</div>
          <div class="grantee-list-item-header" v-for="g in outOfStateBothAgAndNativeAm" :key="g.org_name">
            <v-icon size="12" left color="white">{{ circleIcon }}</v-icon>
            <div>
              {{ g.org_name }}
            </div>
          </div>
        </div>
        <div class="other-a-n-grantee" v-if="outOfStateAgWorkerNotBoth.length > 0">
          <div>Also serving agricultural workers:</div>
          <div class="grantee-list-item-header" v-for="g in outOfStateAgWorkerNotBoth" :key="g.org_name">
            <v-icon size="12" left color="white">{{ circleIcon }}</v-icon>
            <div>
              {{ g.org_name }}
            </div>
          </div>
        </div>
        <div class="other-a-n-grantee" v-if="outOfStateNativeAmNotBoth.length > 0">
          <div>Also serving Native Americans:</div>
          <div class="grantee-list-item-header" v-for="g in outOfStateNativeAmNotBoth" :key="g.org_name">
            <v-icon size="12" left color="white">{{ circleIcon }}</v-icon>
            <div>
              {{ g.org_name }}
            </div>
          </div>
        </div>
        <div class="grantee-list-footer" v-if="agWorkerGrantHeldByBFGeneralRecipient === true || nativeAmGrantHeldByBFGeneralRecipient === true">
          <div v-if="agWorkerGrantHeldByBFGeneralRecipient === true"><div class="a-n-symbol">(A)</div><div class="grantee-list-footer-t">Agricultural worker grant</div></div>
          <div v-if="nativeAmGrantHeldByBFGeneralRecipient === true"><div class="a-n-symbol">(N)</div><div class="grantee-list-footer-t">Native American grant</div></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiCircle, mdiStarOutline } from '@mdi/js';

export default {
  name: 'StateProfilesPrintGranteeList',

  props: {
    selectedState: { type: String, required: true },
    granteesByType: { type: Object, required: true },
    colorPerServArea: { type: Array, required: true },
    officesPerGrantee: { type: Object, required: true }
  },

  data () {
    return {
      circleIcon: mdiCircle,
      mainOfficeIcon: mdiStarOutline
    }
  },

  computed: {
    outOfStateBothAgAndNativeAm () {
      // An out of state grantee (having no BF General grant in the state) may have both the ag worker and native american grants, as with Pine Tree in Connecticut.
      return this.granteesByType.nativeAmNotBfGeneral.filter(g => this.granteesByType.agWorkerNotBfGeneral.map(x => x.org_name).indexOf(g.org_name) > -1)
    },

    outOfStateAgWorkerNotBoth () {
      return this.granteesByType.agWorkerNotBfGeneral.filter(g => this.outOfStateBothAgAndNativeAm.map(y => y.org_name).indexOf(g.org_name) === -1)
    },

    outOfStateNativeAmNotBoth () {
      return this.granteesByType.nativeAmNotBfGeneral.filter(g => this.outOfStateBothAgAndNativeAm.map(y => y.org_name).indexOf(g.org_name) === -1)
    },

    agWorkerGrantHeldByBFGeneralRecipient () {
      return this.granteesByType.agWorker.length > 0 && this.granteesByType.agWorkerNotBfGeneral.length === 0
    },

    nativeAmGrantHeldByBFGeneralRecipient () {
      return this.granteesByType.nativeAm.length > 0 && this.granteesByType.nativeAmNotBfGeneral.length === 0
    },

    mainOfficePerGrantee () {
      let mainOffices = {}
      for (const rno of Object.keys(this.officesPerGrantee)) {
        mainOffices[rno] = this.officesPerGrantee[rno].features.filter(y => y.properties.type === 'Main')[0].properties.city
      }
      return mainOffices
    },

    sortedBranchesPerGrantee () {
      let branchOffices = {}
      for (const rno of Object.keys(this.officesPerGrantee)) {
        let branchFeatures = this.officesPerGrantee[rno].features.filter(y => y.properties.type === 'Branch')
        if (branchFeatures.length === 0) {
          branchOffices[rno] = []
        } else {
          branchOffices[rno] = branchFeatures.map(x => x.properties.city).sort()
        }
      }
      return branchOffices
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
#congr-state-print-grantees {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  .grantee-list-header {
    color: $main-blue;
    margin-top: 0.5rem;
    text-transform: uppercase;
    font-family: "Work Sans", sans-serif;
    font-weight: 400;
    letter-spacing: 0.04rem;
    padding-bottom: 0.25rem;
  }
  .grantee-list {
    font-size: 0.8rem;
    display:flex;
    flex-direction: column;

    .item-color {
      width: 2rem;
      height: 2rem;
    }

    .grantee-list-item-header {
      display: flex;
      align-items: baseline;
    }
  }
  .grantee-list-footer {
    display: flex;
    justify-content: flex-end;
    height: 1rem;
    
    >div {
      margin-left: 1rem;
      display: flex;
      align-items: baseline;
    }
    .grantee-list-footer-t {
      font-style: italic;
      font-size: 0.75rem;
      padding-left: 0.25rem;
      color: #5a5a5a;
    }
  }
  .other-a-n-grantee {
    padding-top: 1rem;

    >div:first-child {
      font-style: italic;
      font-size: 0.75rem;
    }
  }
  .a-n-symbol {
    display: inline-block;
    min-width: 1rem;
    max-width: 1rem;
    min-height: 1rem;
    max-height: 1rem;
    font-size: 0.8rem;
    font-style: italic;
    text-align: center;
    font-weight: 400;
    font-family: 'Work Sans', sans-serif;
    color: #5a5a5a;
    opacity: 0.75;
    margin-left: 0.125rem;
  }
  .main-office .v-icon {
    margin-top: -0.25rem;
    margin-left: 0.25rem;
  }
  .main-office, .branch-offices {
    font-size: 0.75rem;
    color: #5a5a5a;
    margin-left: 2rem;
    padding-top: 0.5rem;

    &:last-of-type {
      padding-bottom: 0.5rem;
    }
  }
}
</style>